import { use } from "react";
import { RecordsContext } from "../../../../scripts/context/accountingData/RecordsCtx";
import { BuContext } from "../../../../scripts/context/BuContext";
import { GenericRecord } from "scripts/models";
import TableUtils, { GenericRecordTableItemCalculator } from "scripts/core/TableUtils";
import { PaymentsContext } from "../../../../scripts/context/accountingData/PaymentsProvider";
import { GQL, Periods } from "@binale-tech/shared";
import { ICCD } from "scripts/models/Interfaces";

export const useSaldoTransferInput = (
    yearTo: number,
    modules: Set<GQL.IProductKey>,
    companyData: Pick<GQL.ICompany, "accountingConfigs" | "accountingYears" | "id">
) => {
    const payments = use(PaymentsContext);
    const { recordsBank, recordsKB, recordsER, recordsAZ, recordsDeb, recordsFE } = use(RecordsContext);
    const { companyBuTimeframes } = use(BuContext);

    if (!companyData) {
        return null;
    }
    const accountingYears = companyData.accountingYears || [];
    const yearFrom = yearTo - 1;
    const accountingConfig = companyData.accountingConfigs.find(v => v.year === yearFrom);

    if (!accountingConfig) {
        return null;
    }

    const getGroupSaldo = (recordsList: GenericRecord[]): number => {
        // yearConfigs doesn't impact saldo calculation, so we take the first one
        const calculator = new GenericRecordTableItemCalculator(recordsList, records =>
            TableUtils.getTableItems(records, payments, companyBuTimeframes, accountingConfig)
        );
        const saldoMap = calculator.getSaldoMap(accountingYears);
        if (saldoMap.has(yearFrom)) {
            return saldoMap.get(yearFrom).get(Periods.Period.LastDayOfYear) ?? 0;
        }
        return 0;
    };
    const getKbs = (): GQL.ICompanyTransferSaldoGroupInput[] => {
        const res: GQL.ICompanyTransferSaldoGroupInput[] = [];
        if (!modules.has(GQL.IProductKey.Kb)) {
            return res;
        }
        for (const [groupId, { list: recordsList }] of recordsKB.groups) {
            const saldo = getGroupSaldo(recordsList);
            if (saldo !== 0) {
                res.push({ groupId, saldo });
            }
        }
        return res;
    };
    const getBanks = (): GQL.ICompanyTransferSaldoGroupInput[] => {
        const res: GQL.ICompanyTransferSaldoGroupInput[] = [];
        if (!modules.has(GQL.IProductKey.Bank)) {
            return res;
        }
        for (const [groupId, { list: recordsList }] of recordsBank.groups) {
            const saldo = getGroupSaldo(recordsList);
            if (saldo !== 0) {
                res.push({ groupId, saldo });
            }
        }
        return res;
    };
    const filterRecord = (record: GenericRecord) => {
        if (record.year !== yearFrom) {
            return false;
        }
        if (record.draft) {
            return false;
        }
        if (record.cancellation) {
            return false;
        }
        return true;
    };
    const getOpenPaymentRecordIds = (recordList: GenericRecord[]): GQL.ICompanyTransferSaldoPaymentsInput => {
        const sourceIds: string[] = [];
        recordList = recordList.filter(filterRecord);
        for (const record of recordList) {
            const openAmount = record.getOpenBrutto(payments.recordRelation.get(record.key));
            if (openAmount === 0) {
                continue;
            }
            sourceIds.push(record.key);
        }

        const bankIds: GQL.IRecordIdIndexInput[] = [];
        const kbIds: GQL.IRecordIdIndexInput[] = [];
        const feIds: GQL.IRecordIdIndexInput[] = [];
        if (sourceIds.length) {
            const processedIds = new Set<string>();
            sourceIds.forEach(id => {
                const connectedPayments = payments.recordRelation.get(id) ?? [];
                connectedPayments.forEach(payment => {
                    processedIds.add(payment.representationRecordKey);
                    processedIds.add(payment.autoFEDiscountRecordKey);
                    // processedIds.add(payment.autoFEBalancingAZRecordKey);
                });
            });
            const isDebitor = recordList[0].getProductKey() === GQL.IProductKey.Deb;
            const filterDisconnected = (record: GenericRecord) => {
                if (!filterRecord(record)) {
                    return false;
                }
                if (processedIds.has(record.key)) {
                    return false;
                }
                if (payments.recordRelation.get(record.key)?.length) {
                    return false;
                }
                return true;
            };
            const addToAccumulator = (
                entity: ICCD,
                accumulator: GQL.IRecordIdIndexInput[],
                recordId: string,
                itemIndex: number
            ) => {
                if (isDebitor) {
                    if (entity.debetor) {
                        accumulator.push({ recordId, itemIndex });
                    }
                } else {
                    if (entity.creditor) {
                        accumulator.push({ recordId, itemIndex });
                    }
                }
            };
            const processPaymentRepresentationRecords = (
                records: GenericRecord[],
                accumulator: GQL.IRecordIdIndexInput[]
            ) => {
                records = records.filter(filterDisconnected);
                for (const record of records) {
                    record.items.forEach((item, index) => {
                        addToAccumulator(item, accumulator, record.key, index);
                    });
                }
            };
            processPaymentRepresentationRecords(recordsBank.combinedList, bankIds);
            processPaymentRepresentationRecords(recordsKB.combinedList, kbIds);
            recordsFE.list.filter(filterDisconnected).forEach(record => {
                if (record.debetor || record.creditor) {
                    if (record.items.length > 1) {
                        console.log("FE record has more than one item", record);
                        return;
                        // throw new Error("FE record has more than one item");
                    }
                    addToAccumulator(record, feIds, record.key, 0);
                }
            });
        }
        return { sourceIds, bankIds, kbIds, feIds };
    };
    const getER = (): GQL.ICompanyTransferSaldoPaymentsInput => {
        if (!modules.has(GQL.IProductKey.Er)) {
            return null;
        }
        return getOpenPaymentRecordIds(recordsER.list);
    };
    const getErA = (): GQL.ICompanyTransferSaldoPaymentsInput => {
        if (!modules.has(GQL.IProductKey.ErA)) {
            return null;
        }
        return getOpenPaymentRecordIds(recordsAZ.list);
    };
    const getDeb = (): GQL.ICompanyTransferSaldoPaymentsInput => {
        if (!modules.has(GQL.IProductKey.Deb)) {
            return null;
        }
        return getOpenPaymentRecordIds(recordsDeb.list);
    };
    const input: GQL.ICompanyTransferSaldoInput = {
        yearTo,
        companyId: companyData?.id,
        kb: getKbs(),
        bank: getBanks(),
        er: getER(),
        erAnz: getErA(),
        deb: getDeb(),
    };
    return input;
};
